import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo } from 'react';
import UI from '../../../ui';
import StateIcon from '../../../ui/icon/StateIcon.component';
import Spinner from '../../../ui/Spinner.component';
import { USPS_CONSTANTS } from '../constants';
import { useUSPSForm } from '../hooks/useUSPSForm';

const { WrappedDropdownWithLabel, WrappedButton, WrappedButtonType } = UI;

const FormField = ({
	label,
	name,
	value,
	isEditMode,
	validation,
	options,
	onChange,
	placeholder,
	dataqa,
	fullWidth = false,
}) => (
	<div className={`form-field ${fullWidth ? 'form-field--full' : ''}`}>
		<label htmlFor={name}>{label}</label>
		{isEditMode ? (
			<WrappedDropdownWithLabel
				errorMessage="This field is required"
				className="dropdown"
				validation={validation}
				uiElementConfig={{
					value,
					options,
					optionLabel: 'label',
					optionValue: 'value',
					onChange,
					placeholder,
					dataqa,
					name,
				}}
			/>
		) : (
			<div className="field-value">{value || 'Not set'}</div>
		)}
	</div>
);

export const FormSection = ({
	onboardingStatus,
	userInfoToken,
	isIDsSaved,
	setIDsSaved,
	isUSPSAccountCreated,
	refetch,
	clearUserInfoToken,
}) => {
	const {
		formData,
		validations,
		isLoading,
		availableCrids,
		selectableMids,
		availableEpsAccounts,
		onChangeSelect,
		submitForm,
	} = useUSPSForm(onboardingStatus, userInfoToken);
	const [isInEditMode, setIsInEditMode] = React.useState(
		!isEmpty(userInfoToken)
	);

	// Sync isInEditMode when userInfoToken changes
	useEffect(() => {
		setIsInEditMode(!isEmpty(userInfoToken));
	}, [userInfoToken]);

	const handleSaveClick = useCallback(async () => {
		const success = await submitForm();
		if (success) {
			setIDsSaved(true);
			setIsInEditMode(false);
			clearUserInfoToken();
			refetch();
		}
	}, [submitForm, setIDsSaved, refetch, clearUserInfoToken]);

	const isButtonDisabled = useMemo(() => {
		return (
			isLoading ||
			validations[USPS_CONSTANTS.FORM_FIELDS.CRID_NUMBER] !==
				USPS_CONSTANTS.VALIDATION_STATES.VALID ||
			validations[USPS_CONSTANTS.FORM_FIELDS.MID_NUMBER] !==
				USPS_CONSTANTS.VALIDATION_STATES.VALID ||
			validations[USPS_CONSTANTS.FORM_FIELDS.EPS_ACCOUNT_NUMBER] !==
				USPS_CONSTANTS.VALIDATION_STATES.VALID
		);
	}, [isLoading, validations]);

	const sectionClassName = useMemo(
		() => `form-section form-section--${isInEditMode ? 'edit' : 'show'}`,
		[isInEditMode]
	);

	const buttonConfig = useMemo(
		() => ({
			save: {
				buttonType: WrappedButtonType.Primary,
				label: 'Save',
				onClick: handleSaveClick,
				disabled: isButtonDisabled,
				dataqa: 'save-usps-connection-button',
			},
		}),
		[handleSaveClick, isButtonDisabled]
	);

	const fields = [
		{
			label: 'CRID #',
			name: USPS_CONSTANTS.FORM_FIELDS.CRID_NUMBER,
			options: availableCrids,
			placeholder: 'Select Crid #',
			dataqa: 'cridNumber',
		},
		{
			label: 'Manifest/Master MID',
			name: USPS_CONSTANTS.FORM_FIELDS.MID_NUMBER,
			options: selectableMids,
			placeholder: 'Select Mid #',
			dataqa: 'midNumber',
		},
		{
			label: 'EPS Account #',
			name: USPS_CONSTANTS.FORM_FIELDS.EPS_ACCOUNT_NUMBER,
			options: availableEpsAccounts,
			placeholder: 'Select One',
			dataqa: 'epsAccountNumber',
			fullWidth: true,
		},
	];

	return (
		<section className="usps-form-section">
			<article className={sectionClassName}>
				<header className="form-header">
					<StateIcon isCompleted={isUSPSAccountCreated && !isInEditMode} />
					<h4 id="form-heading">
						{isUSPSAccountCreated
							? 'Input your CRID, MID and EPS IDs:'
							: 'Then you will be prompted to enter your CRID, MID and EPS IDs.'}
					</h4>
				</header>
				{isUSPSAccountCreated && (
					<div className="form-body">
						<p className="form-description">
							After inputting your IDs, please wait for approval from USPS to
							begin printing with NSA rates.
						</p>

						<div className="form-fields">
							{isLoading ? (
								<Spinner msg="Loading CRID, MID and EPS IDs" mask={false} />
							) : (
								fields.map(field => (
									<FormField
										key={field.name}
										label={field.label}
										name={field.name}
										value={formData[field.name]}
										isEditMode={isInEditMode}
										validation={validations[field.name]}
										options={field.options}
										onChange={onChangeSelect}
										placeholder={field.placeholder}
										dataqa={field.dataqa}
										fullWidth={field.fullWidth}
									/>
								))
							)}
						</div>

						{isInEditMode && (
							<footer className="form-action">
								<WrappedButton {...buttonConfig.save} />
							</footer>
						)}
					</div>
				)}
			</article>
		</section>
	);
};
